import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { MapPin } from 'lucide-react'
import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useParams } from 'react-router-dom'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import {
  getCareersJobPosting,
  submitCareerApplication,
  uploadCareerResume,
} from '@/features/marketplace/careers'
import { useDocumentMeta } from '@/lib/documentMeta'

const schema = z.object({
  full_name: z.string().trim().min(1, 'Enter your name').max(150),
  email: z.string().trim().min(1, 'Enter your email').email('Enter a valid email').max(255),
  phone: z.string().trim().max(30),
})

type FormValues = z.infer<typeof schema>

/**
 * One open posting plus the apply form -- name, email, phone, an optional résumé, no
 * account. Submitting creates a real candidate and application directly in HR's live
 * pipeline (`POST /marketplace/careers/applications`); there is nothing to approve
 * afterwards, so the confirmation here is the whole story.
 */
export function StorefrontCareerDetail() {
  const { jobPostingId } = useParams<{ jobPostingId: string }>()
  const [formError, setFormError] = useState<string | null>(null)
  const [resumeKey, setResumeKey] = useState<string | null>(null)
  const [resumeName, setResumeName] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  const {
    data: posting,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['careers-job-posting', jobPostingId],
    queryFn: () => getCareersJobPosting(jobPostingId!),
    enabled: !!jobPostingId,
    retry: false,
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { full_name: '', email: '', phone: '' },
  })

  useDocumentMeta(
    posting ? `${posting.title} — Careers` : undefined,
    posting?.description ?? undefined,
  )

  const pickResume = async (file: File) => {
    setUploading(true)
    try {
      const key = await uploadCareerResume(file)
      setResumeKey(key)
      setResumeName(file.name)
    } catch (uploadError) {
      setFormError(
        uploadError instanceof ApiError ? uploadError.message : 'The résumé could not be uploaded.',
      )
    } finally {
      setUploading(false)
    }
  }

  const apply = useMutation({
    mutationFn: (values: FormValues) =>
      submitCareerApplication({
        job_posting_id: jobPostingId!,
        full_name: values.full_name,
        email: values.email,
        phone: values.phone || null,
        resume_key: resumeKey,
      }),
    onSuccess: () => {
      setSubmitted(true)
      reset()
      setResumeKey(null)
      setResumeName(null)
    },
    onError: (submitError: unknown) => {
      setFormError(
        submitError instanceof ApiError
          ? submitError.status === 409
            ? 'You already have an open application for this job.'
            : submitError.message
          : 'Could not reach the server. Try again.',
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    return apply.mutateAsync(values).catch(() => {
      /* surfaced in formError by onError */
    })
  })

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="h-6 w-6 text-slate-400" />
      </div>
    )
  }

  if (error || !posting) {
    return (
      <div className="space-y-4">
        <Alert tone="error" title="Job posting not found">
          This role may have closed.
        </Alert>
        <Link to="/store/careers" className="inline-block">
          <Button variant="secondary">Back to careers</Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Link
        to="/store/careers"
        className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
      >
        ← Back to careers
      </Link>

      <div className="grid gap-6 sm:grid-cols-2">
        <div className="space-y-3">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            {posting.title}
          </h1>
          {posting.department_name && (
            <p className="text-sm text-slate-500 dark:text-slate-400">{posting.department_name}</p>
          )}
          {posting.location_name && (
            <p className="flex items-center gap-1.5 text-sm text-slate-400">
              <MapPin className="h-4 w-4" aria-hidden="true" />
              {posting.location_name}
            </p>
          )}
          {posting.description && (
            <p className="whitespace-pre-line text-sm leading-relaxed text-slate-600 dark:text-slate-400">
              {posting.description}
            </p>
          )}
        </div>

        <Card>
          <CardBody className="space-y-4">
            {submitted ? (
              <Alert tone="success" title="Application received">
                Thanks for applying. Our HR team will be in touch if you're a match.
              </Alert>
            ) : (
              <form onSubmit={onSubmit} noValidate className="space-y-4">
                <h2 className="font-medium text-slate-900 dark:text-slate-100">Apply for this role</h2>

                {formError && <Alert tone="error">{formError}</Alert>}

                <Field label="Full name" error={errors.full_name?.message}>
                  {(props) => <Input {...props} {...register('full_name')} autoComplete="name" />}
                </Field>
                <Field label="Email" error={errors.email?.message}>
                  {(props) => (
                    <Input {...props} {...register('email')} type="email" autoComplete="email" />
                  )}
                </Field>
                <Field label="Phone" error={errors.phone?.message} hint="Optional.">
                  {(props) => <Input {...props} {...register('phone')} autoComplete="tel" />}
                </Field>

                <Field label="Résumé" hint="PDF or DOCX. Optional.">
                  {() => (
                    <div className="flex items-center gap-3">
                      <input
                        ref={fileInput}
                        type="file"
                        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        className="sr-only"
                        onChange={(event) => {
                          const file = event.target.files?.[0]
                          event.target.value = ''
                          if (file) void pickResume(file)
                        }}
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        loading={uploading}
                        onClick={() => fileInput.current?.click()}
                      >
                        {resumeKey ? 'Replace résumé' : 'Upload résumé'}
                      </Button>
                      {resumeName && (
                        <span className="text-sm text-slate-500 dark:text-slate-400">
                          {resumeName}
                        </span>
                      )}
                    </div>
                  )}
                </Field>

                <Button type="submit" loading={isSubmitting} className="w-full">
                  Submit application
                </Button>
              </form>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  )
}
